import React, {useId} from 'react';
import {Box, useComputedColorScheme, useMantineTheme} from '@mantine/core';

/**
 * Article artwork, drawn rather than loaded.
 *
 * Each article names a scene that depicts what it is actually about — a
 * branching history for prompt versioning, a waveform for latency, a stack of
 * context planes, and so on. Everything is inline SVG built from theme colours,
 * so there is no network request, both colour schemes are correct by
 * construction, and nothing can go stale when an article is rewritten.
 *
 * Filter and gradient ids are namespaced per instance with useId(); duplicated
 * ids across cards would make every card render the first card's gradients.
 */
export type ArtScene =
    | 'constellation'   // the studio / shared platform
    | 'branches'        // versioning and approval history
    | 'transcript'      // a structured record of a conversation
    | 'waveform'        // speech, latency, response time
    | 'layers'          // assembled context
    | 'seal'            // regulation and disclosure
    | 'dwelling'        // property and hospitality
    | 'chat'            // guest questions
    | 'checklist'       // evaluation criteria
    | 'portfolio';      // the four ventures

type Tone = 'brand' | 'accent' | 'mixed';

const ArticleArt = ({
    scene = 'constellation',
    tone = 'brand',
    seed = 0,
    height = '100%'
}: {
    scene?: ArtScene;
    tone?: Tone;
    seed?: number;
    height?: number | string;
}) => {
    const theme = useMantineTheme();
    const isDark = useComputedColorScheme('light') === 'dark';
    const uid = useId().replace(/:/g, '');

    // Two background stops plus a highlight used for the glow and the subject.
    const palettes: Record<Tone, [string, string, string]> = {
        brand: isDark
            ? [theme.colors.brand[9], '#101A2B', theme.colors.brand[4]]
            : [theme.colors.brand[2], theme.colors.brand[6], theme.colors.brand[0]],
        accent: isDark
            ? [theme.colors.accent[7], '#1A1206', theme.colors.accent[1]]
            : [theme.colors.accent[0], theme.colors.accent[4], '#FFF7EA'],
        mixed: isDark
            ? [theme.colors.brand[9], theme.colors.accent[8], theme.colors.brand[3]]
            : [theme.colors.brand[2], theme.colors.accent[2], theme.colors.brand[0]]
    };

    const [bgFrom, bgTo, highlight] = palettes[tone];
    const ink = isDark ? theme.colors.gray[0] : theme.white;
    const angle = 118 + (seed % 4) * 28;

    // Opacities are tuned separately per scheme: the same value reads as heavy
    // on a light wash and invisible on a dark one.
    const line = isDark ? 0.72 : 0.86;
    const soft = isDark ? 0.2 : 0.3;
    const faint = isDark ? 0.1 : 0.16;

    const g = `url(#glow-${uid})`;

    const scenes: Record<ArtScene, React.ReactNode> = {
        constellation: (
            <g stroke={ink} fill="none" strokeLinecap="round">
                <circle cx={200} cy={95} r={62} opacity={faint} strokeWidth={1}/>
                <circle cx={200} cy={95} r={96} opacity={faint * 0.7} strokeWidth={1}/>
                <g opacity={line * 0.55} strokeWidth={1.25}>
                    <path d="M200 95 L118 52 M200 95 L292 48 M200 95 L108 140 M200 95 L296 142 M118 52 L108 140 M292 48 L296 142"/>
                </g>
                <g filter={g}>
                    <circle cx={200} cy={95} r={13} fill={ink} opacity={line}/>
                    <circle cx={200} cy={95} r={22} stroke={ink} opacity={soft} strokeWidth={1.5}/>
                </g>
                {[[118, 52], [292, 48], [108, 140], [296, 142]].map(([cx, cy], i) => (
                    <circle key={i} cx={cx} cy={cy} r={7.5} fill={ink} opacity={line * 0.9} filter={g}/>
                ))}
            </g>
        ),
        branches: (
            <g stroke={ink} fill="none" strokeLinecap="round">
                <path d="M52 128 H348" strokeWidth={2} opacity={line * 0.8}/>
                <path d="M120 128 C150 128 150 74 182 74 H286" strokeWidth={2} opacity={line * 0.6}/>
                <path d="M214 74 C246 74 246 128 278 128" strokeWidth={2} opacity={line * 0.45} strokeDasharray="5 6"/>
                <path d="M156 128 C186 128 186 168 218 168 H300" strokeWidth={2} opacity={line * 0.35}/>
                {[[86, 128], [120, 128], [156, 128], [214, 128], [278, 128], [330, 128]].map(([cx, cy], i) => (
                    <circle key={i} cx={cx} cy={cy} r={i === 4 ? 9 : 6.5} fill={ink} opacity={line} filter={i === 4 ? g : undefined}/>
                ))}
                {[[182, 74], [248, 74], [286, 74]].map(([cx, cy], i) => (
                    <circle key={`b${i}`} cx={cx} cy={cy} r={6} fill={ink} opacity={line * 0.8}/>
                ))}
                <circle cx={218} cy={168} r={5.5} fill={ink} opacity={line * 0.5}/>
            </g>
        ),
        transcript: (
            <g fill="none" stroke={ink} strokeLinecap="round">
                <rect x={96} y={30} width={208} height={132} rx={12} opacity={soft} strokeWidth={1.5} fill={ink} fillOpacity={faint * 0.5}/>
                {[0, 1, 2, 3, 4].map((row) => (
                    <path
                        key={row}
                        d={`M122 ${60 + row * 21} H${
                            [252, 278, 226, 266, 208][row]
                        }`}
                        strokeWidth={row === 0 ? 5 : 3.5}
                        opacity={row === 0 ? line : line * (0.62 - row * 0.07)}
                    />
                ))}
                <g filter={g}>
                    <circle cx={302} cy={132} r={26} fill={ink} opacity={line}/>
                    <path d="M302 122 v10 M296 132 a6 6 0 0 0 12 0" stroke={bgTo} strokeWidth={2.4} opacity={0.9}/>
                    <rect x={298} y={118} width={8} height={13} rx={4} fill={bgTo} opacity={0.9} stroke="none"/>
                </g>
            </g>
        ),
        waveform: (
            <g stroke={ink} strokeLinecap="round" fill="none">
                <path d="M40 95 H360" strokeWidth={1} opacity={faint}/>
                {Array.from({length: 34}).map((_, i) => {
                    // A deterministic pseudo-envelope: two sines beating against
                    // each other, so it reads as speech rather than a bar chart.
                    const t = i / 33;
                    const amp = Math.abs(Math.sin(t * Math.PI * 3.1 + seed) * 0.62 + Math.sin(t * Math.PI * 7.7) * 0.32);
                    const h = 8 + amp * 68;
                    const x = 46 + i * 9;
                    const active = i < 22;
                    return (
                        <path
                            key={i}
                            d={`M${x} ${95 - h / 2} V${95 + h / 2}`}
                            strokeWidth={4.5}
                            opacity={active ? line : line * 0.28}
                            filter={active && i > 16 ? g : undefined}
                        />
                    );
                })}
                <path d="M244 40 V150" strokeWidth={1.5} opacity={soft} strokeDasharray="4 5"/>
            </g>
        ),
        layers: (
            <g stroke={ink} fill="none">
                {[0, 1, 2, 3].map((i) => (
                    <g key={i} opacity={1 - i * 0.18}>
                        <path
                            d={`M200 ${44 + i * 30} l104 26 l-104 26 l-104 -26 z`}
                            fill={ink}
                            fillOpacity={faint * (1.5 - i * 0.28)}
                            strokeWidth={1.4}
                            opacity={line * (0.9 - i * 0.16)}
                            filter={i === 0 ? g : undefined}
                        />
                    </g>
                ))}
                <path d="M200 26 V44" strokeWidth={2} opacity={line * 0.7} strokeLinecap="round"/>
                <circle cx={200} cy={22} r={5} fill={ink} opacity={line} stroke="none"/>
            </g>
        ),
        seal: (
            <g stroke={ink} fill="none" strokeLinecap="round">
                <rect x={112} y={28} width={140} height={136} rx={10} strokeWidth={1.5} opacity={soft} fill={ink} fillOpacity={faint * 0.5}/>
                {[0, 1, 2, 3].map((row) => (
                    <path key={row} d={`M136 ${58 + row * 22} H${[218, 228, 196, 210][row]}`} strokeWidth={3.4} opacity={line * (0.6 - row * 0.08)}/>
                ))}
                <g filter={g}>
                    <circle cx={272} cy={118} r={34} fill={ink} fillOpacity={line * 0.16} strokeWidth={2} opacity={line}/>
                    <circle cx={272} cy={118} r={23} strokeWidth={1.5} opacity={line * 0.7}/>
                    {Array.from({length: 12}).map((_, i) => {
                        const a = (i / 12) * Math.PI * 2;
                        return (
                            <circle
                                key={i}
                                cx={272 + Math.cos(a) * 29}
                                cy={118 + Math.sin(a) * 29}
                                r={2.4}
                                fill={ink}
                                stroke="none"
                                opacity={line}
                            />
                        );
                    })}
                    <path d="M262 118 l7 8 l14 -17" strokeWidth={3} opacity={line}/>
                </g>
            </g>
        ),
        dwelling: (
            <g stroke={ink} fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M78 168 H322" strokeWidth={1.5} opacity={soft}/>
                <g opacity={line * 0.35} strokeWidth={1.6}>
                    <path d="M96 168 V96 l52 -34 l52 34 V168"/>
                </g>
                <g strokeWidth={2} opacity={line}>
                    <path d="M170 168 V88 l64 -42 l64 42 V168" filter={g}/>
                    <path d="M156 92 L234 40 L312 92" strokeWidth={2.4}/>
                </g>
                {[[200, 106], [252, 106], [200, 140], [252, 140]].map(([x, y], i) => (
                    <rect
                        key={i}
                        x={x - 13}
                        y={y - 11}
                        width={26}
                        height={22}
                        rx={3}
                        fill={ink}
                        fillOpacity={i === 1 ? line * 0.55 : faint * 1.4}
                        strokeWidth={1.2}
                        opacity={line * 0.7}
                        filter={i === 1 ? g : undefined}
                    />
                ))}
            </g>
        ),
        chat: (
            <g stroke={ink} fill="none" strokeLinecap="round">
                <g filter={g}>
                    <path
                        d="M100 44 h150 a14 14 0 0 1 14 14 v46 a14 14 0 0 1 -14 14 h-96 l-28 24 v-24 h-26 a14 14 0 0 1 -14 -14 v-46 a14 14 0 0 1 14 -14 z"
                        fill={ink}
                        fillOpacity={faint * 1.6}
                        strokeWidth={1.8}
                        opacity={line}
                    />
                </g>
                <path d="M128 72 H236 M128 94 H206" strokeWidth={3.6} opacity={line * 0.6}/>
                <path
                    d="M300 96 h-84 a12 12 0 0 0 -12 12 v34 a12 12 0 0 0 12 12 h60 l24 20 v-20 h0 a12 12 0 0 0 12 -12 v-34 a12 12 0 0 0 -12 -12 z"
                    fill={ink}
                    fillOpacity={faint}
                    strokeWidth={1.6}
                    opacity={line * 0.75}
                />
                <path d="M228 120 H296 M228 138 H272" strokeWidth={3.2} opacity={line * 0.45}/>
            </g>
        ),
        checklist: (
            <g stroke={ink} fill="none" strokeLinecap="round" strokeLinejoin="round">
                <rect x={104} y={26} width={192} height={140} rx={12} strokeWidth={1.5} opacity={soft} fill={ink} fillOpacity={faint * 0.5}/>
                {[0, 1, 2, 3].map((row) => {
                    const y = 58 + row * 30;
                    const done = row < 3;
                    return (
                        <g key={row} opacity={done ? line : line * 0.4}>
                            <rect
                                x={130}
                                y={y - 11}
                                width={22}
                                height={22}
                                rx={6}
                                strokeWidth={1.8}
                                fill={ink}
                                fillOpacity={done ? line * 0.5 : 0}
                                filter={row === 0 ? g : undefined}
                            />
                            {done && <path d={`M135 ${y} l5 6 l10 -12`} stroke={bgTo} strokeWidth={2.6}/>}
                            <path d={`M168 ${y} H${[262, 246, 270, 232][row]}`} strokeWidth={3.4} opacity={0.72}/>
                        </g>
                    );
                })}
            </g>
        ),
        portfolio: (
            <g stroke={ink} fill="none" strokeLinecap="round">
                <path d="M200 95 H128 M200 95 H272 M200 95 V52 M200 95 V138" strokeWidth={1.3} opacity={line * 0.4} strokeDasharray="4 5"/>
                {[[128, 52], [272, 52], [128, 138], [272, 138]].map(([cx, cy], i) => (
                    <rect
                        key={i}
                        x={cx - 40}
                        y={cy - 26}
                        width={80}
                        height={52}
                        rx={10}
                        strokeWidth={1.8}
                        fill={ink}
                        fillOpacity={i === 0 ? faint * 2.4 : faint}
                        opacity={line * (i === 0 ? 1 : 0.72)}
                        filter={i === 0 ? g : undefined}
                    />
                ))}
                <circle cx={200} cy={95} r={17} fill={ink} fillOpacity={line * 0.9} strokeWidth={0} filter={g}/>
                <circle cx={200} cy={95} r={27} strokeWidth={1.4} opacity={soft}/>
            </g>
        )
    };

    // The background is CSS so it fills any aspect ratio edge to edge, while the
    // SVG scene scales with `meet` and is never cropped. Doing both inside the
    // SVG with `slice` clipped the subject on wide cards.
    const gridInk = isDark ? `rgba(255,255,255,0.07)` : `rgba(255,255,255,0.14)`;
    const background = [
        `radial-gradient(120% 90% at 22% 12%, ${highlight}${isDark ? '80' : 'D9'} 0%, transparent 62%)`,
        `radial-gradient(100% 80% at 88% 98%, ${highlight}${isDark ? '52' : '8C'} 0%, transparent 58%)`,
        `repeating-linear-gradient(0deg, ${gridInk} 0 1px, transparent 1px 26px)`,
        `repeating-linear-gradient(90deg, ${gridInk} 0 1px, transparent 1px 26px)`,
        `linear-gradient(${angle}deg, ${bgFrom} 0%, ${bgTo} 100%)`
    ].join(', ');

    return (
        <Box
            aria-hidden
            style={{
                height,
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
                position: 'relative',
                background,
                boxShadow: isDark
                    ? 'inset 0 -40px 60px -30px rgba(0,0,0,0.55)'
                    : 'inset 0 -40px 60px -30px rgba(0,0,0,0.18)'
            }}
        >
            <svg
                width="100%"
                height="100%"
                viewBox="0 0 400 190"
                preserveAspectRatio="xMidYMid meet"
                style={{display: 'block', position: 'relative'}}
            >
                <defs>
                    <filter id={`glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
                        <feGaussianBlur stdDeviation="5" result="blur"/>
                        <feMerge>
                            <feMergeNode in="blur"/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                </defs>

                {scenes[scene]}
            </svg>
        </Box>
    );
};

export default ArticleArt;
